import { useEffect, useState } from "react";
import type { Locale, SessionUser, TenantSummary, ThemeKey } from "../shared/model";
import { THEMES } from "../shared/themeCatalog";
import { api } from "./api";
import { TenantWorkspace } from "./TenantWorkspace";
import { Building2, Globe2, LogOut, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem,
  SidebarProvider, SidebarRail, SidebarTrigger,
} from "@/components/ui/sidebar";

export function App() {
  const [loading, setLoading] = useState(true);
  const [bootstrapRequired, setBootstrapRequired] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const status = await api.bootstrapStatus();
        setBootstrapRequired(status.required);
        if (!status.required) {
          try { setUser((await api.me()).user); } catch { setUser(null); }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="app-loading"><span className="spinner" /> CMS hazırlanıyor…</div>;
  if (!user) {
    return <AuthScreen bootstrapRequired={bootstrapRequired} onAuthenticated={(nextUser) => { setUser(nextUser); setBootstrapRequired(false); }} />;
  }
  if (user.mustChangePassword) {
    return <ChangePasswordScreen user={user} onChanged={() => setUser({ ...user, mustChangePassword: false })} onLogout={async () => { await api.logout(); setUser(null); }} />;
  }
  return <Dashboard user={user} onLogout={async () => { await api.logout(); setUser(null); }} />;
}

function ChangePasswordScreen({ user, onChanged, onLogout }: { user: SessionUser; onChanged: () => void; onLogout: () => Promise<void> }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return <main className="auth-page">
    <section className="auth-copy"><div className="brand-mark">W</div><p className="eyebrow">Güvenli hesap</p><h1>Geçici şifrenizi değiştirin.</h1><p>{user.displayName}, tenant paneline devam etmeden önce yalnız sizin bildiğiniz yeni bir şifre belirleyin.</p></section>
    <section className="auth-card-wrap"><form className="auth-card" onSubmit={async (event) => { event.preventDefault(); setSaving(true); setError(""); try { await api.changePassword(currentPassword, password); onChanged(); } catch (reason) { setError(reason instanceof Error ? reason.message : "Şifre değiştirilemedi."); } finally { setSaving(false); } }}>
      <p className="eyebrow">İlk giriş</p><h2>Yeni şifre belirle</h2>
      <label>Geçici şifre<input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} minLength={10} required /></label>
      <label>Yeni şifre<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={10} required /></label>
      {error && <div className="alert error">{error}</div>}
      <button className="button primary wide" disabled={saving}>{saving ? "Kaydediliyor…" : "Şifreyi değiştir"}</button>
      <button type="button" className="button ghost wide auth-secondary" onClick={() => void onLogout()}>Çıkış yap</button>
    </form></section>
  </main>;
}

function AuthScreen({ bootstrapRequired, onAuthenticated }: { bootstrapRequired: boolean; onAuthenticated: (user: SessionUser) => void }) {
  const [email, setEmail] = useState(bootstrapRequired ? "owner@webhub.local" : "");
  const [displayName, setDisplayName] = useState("Platform Sahibi");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (bootstrapRequired) await api.bootstrap({ email, displayName, password, token });
      const result = await api.login(email, password);
      onAuthenticated(result.user);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Giriş yapılamadı.");
    } finally {
      setSubmitting(false);
    }
  }

  return <main className="auth-page">
    <section className="auth-copy">
      <div className="brand-mark">W</div>
      <p className="eyebrow">Cloudflare üzerinde çok kiracılı CMS</p>
      <h1>Her müşteriye özel site.<br />Tek güçlü panel.</h1>
      <p>Creative Agency 2, Accounting 1 ve Business Consulting 3 temalarını tek Worker üzerinden yönetin.</p>
      <div className="auth-points"><span>D1 içerik veritabanı</span><span>R2 medya deposu</span><span>TR / EN yayın</span></div>
    </section>
    <section className="auth-card-wrap"><form className="auth-card" onSubmit={submit}>
      <p className="eyebrow">{bootstrapRequired ? "İlk kurulum" : "Yönetim paneli"}</p>
      <h2>{bootstrapRequired ? "Platform sahibini oluştur" : "Tekrar hoş geldiniz"}</h2>
      {bootstrapRequired && <label>Ad soyad<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required minLength={2} /></label>}
      <label>E-posta<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
      <label>Şifre<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={10} /></label>
      {bootstrapRequired && <label>Bootstrap anahtarı<input type="password" value={token} onChange={(event) => setToken(event.target.value)} required minLength={16} /><small>Yerelde `.dev.vars`, canlıda Cloudflare secret değeridir.</small></label>}
      {error && <div className="alert error">{error}</div>}
      <button className="button primary wide" disabled={submitting}>{submitting ? "İşleniyor…" : bootstrapRequired ? "Kurulumu tamamla" : "Giriş yap"}</button>
    </form></section>
  </main>;
}

function Dashboard({ user, onLogout }: { user: SessionUser; onLogout: () => Promise<void> }) {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  async function refreshTenants(select?: string) {
    const result = await api.tenants();
    setTenants(result.tenants);
    setSelectedId(select ?? selectedId ?? result.tenants[0]?.id ?? null);
    setLoading(false);
  }

  useEffect(() => { void refreshTenants(); }, []);
  const selected = tenants.find((tenant) => tenant.id === selectedId) ?? null;

  return <SidebarProvider className="webhub-shell" style={{ "--sidebar-width": "17rem", "--sidebar-width-icon": "3.5rem" } as React.CSSProperties}>
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader className="border-b border-sidebar-border p-3">
        <SidebarMenu><SidebarMenuItem><SidebarMenuButton size="lg" tooltip="WebHub Cloud CMS" className="hover:bg-transparent"><span className="grid size-8 shrink-0 place-items-center rounded-lg bg-sidebar-primary font-bold text-sidebar-primary-foreground">W</span><span className="grid min-w-0 flex-1 text-left leading-tight"><strong className="truncate text-sm">WebHub</strong><small className="truncate text-[10px] text-muted-foreground">Cloud CMS</small></span></SidebarMenuButton></SidebarMenuItem></SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Siteler</SidebarGroupLabel>
          <SidebarGroupContent><SidebarMenu>{tenants.map((tenant) => <SidebarMenuItem key={tenant.id}><SidebarMenuButton size="lg" tooltip={tenant.name} isActive={selectedId === tenant.id} onClick={() => setSelectedId(tenant.id)}><span className="grid size-7 shrink-0 place-items-center rounded-md border bg-background text-[11px] font-semibold">{tenant.name.slice(0, 1).toUpperCase()}</span><span className="grid min-w-0 flex-1 text-left leading-tight"><strong className="truncate text-xs">{tenant.name}</strong><small className="truncate text-[10px] text-muted-foreground">{THEMES[tenant.themeKey].name}</small></span><SidebarMenuBadge><i className={`status-dot ${tenant.status}`} /></SidebarMenuBadge></SidebarMenuButton></SidebarMenuItem>)}</SidebarMenu></SidebarGroupContent>
        </SidebarGroup>
        {user.role === "platform_owner" && <SidebarGroup className="mt-auto"><SidebarGroupContent><SidebarMenu><SidebarMenuItem><SidebarMenuButton tooltip="Yeni tenant" onClick={() => setCreateOpen(true)}><Plus /><span>Yeni tenant</span></SidebarMenuButton></SidebarMenuItem></SidebarMenu></SidebarGroupContent></SidebarGroup>}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-3">
        <SidebarMenu><SidebarMenuItem><SidebarMenuButton size="lg" tooltip={user.displayName}><span className="grid size-7 shrink-0 place-items-center rounded-full bg-foreground text-[11px] font-semibold text-background">{user.displayName.slice(0, 1).toUpperCase()}</span><span className="grid min-w-0 flex-1 text-left leading-tight"><strong className="truncate text-xs">{user.displayName}</strong><small className="truncate text-[10px] text-muted-foreground">{user.role === "platform_owner" ? "Platform sahibi" : "Tenant editörü"}</small></span></SidebarMenuButton></SidebarMenuItem><SidebarMenuItem><SidebarMenuButton tooltip="Çıkış yap" onClick={() => void onLogout()}><LogOut /><span>Çıkış yap</span></SidebarMenuButton></SidebarMenuItem></SidebarMenu>
      </SidebarFooter><SidebarRail />
    </Sidebar>
    <SidebarInset className="min-w-0 overflow-hidden">
      <header className="cms-topbar"><div className="flex min-w-0 items-center gap-2"><SidebarTrigger /><span className="cms-topbar-separator" /><Globe2 className="size-4 text-muted-foreground" /><strong className="truncate text-sm">{selected?.name ?? "WebHub"}</strong></div><div className="flex items-center gap-2 text-xs text-muted-foreground"><Building2 className="size-4" /><span>{tenants.length} site</span>{user.role === "platform_owner" && <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}><Plus data-icon="inline-start" />Yeni site</Button>}</div></header>
      <div className="dashboard-main">
      {loading ? <div className="app-loading"><span className="spinner" /> Tenant’lar yükleniyor…</div> : selected ? <TenantWorkspace key={selected.id} user={user} tenant={selected} onTenantChanged={(next) => { setTenants((current) => current.map((item) => item.id === next.id ? next : item)); }} /> : <EmptyState owner={user.role === "platform_owner"} onCreate={() => setCreateOpen(true)} />}
      </div>
    </SidebarInset>
    {createOpen && <CreateTenantDialog onClose={() => setCreateOpen(false)} onCreated={async (tenant) => { setCreateOpen(false); await refreshTenants(tenant.id); }} />}
  </SidebarProvider>;
}

function EmptyState({ owner, onCreate }: { owner: boolean; onCreate: () => void }) {
  return <section className="empty-state"><div className="empty-icon">W</div><h2>Henüz yönetilecek site yok</h2><p>{owner ? "İlk tenant’ı oluşturup üç Porto temasından birini atayın." : "Platform sahibi henüz hesabınıza bir site atamadı."}</p>{owner && <button className="button primary" onClick={onCreate}>İlk tenant’ı oluştur</button>}</section>;
}

function CreateTenantDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (tenant: TenantSummary) => Promise<void> }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [themeKey, setThemeKey] = useState<ThemeKey>("creative-agency-2");
  const [defaultLocale, setDefaultLocale] = useState<Locale>("tr");
  const [quota, setQuota] = useState(250);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault(); setSaving(true); setError("");
    try {
      const result = await api.createTenant({ name, slug, themeKey, defaultLocale, mediaQuotaBytes: quota * 1024 * 1024 });
      await onCreated(result.tenant);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Tenant oluşturulamadı."); }
    finally { setSaving(false); }
  }

  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><form className="modal" onSubmit={submit}>
    <div className="modal-head"><div><p className="eyebrow">Yeni site</p><h2>Tenant oluştur</h2></div><button type="button" className="icon-button" onClick={onClose}>×</button></div>
    <div className="form-grid two"><label>Müşteri / site adı<input value={name} onChange={(event) => { setName(event.target.value); if (!slug) setSlug(toSlug(event.target.value)); }} required /></label><label>Demo adresi<input value={slug} onChange={(event) => setSlug(toSlug(event.target.value))} required /><small>/s/{slug || "musteri"}</small></label></div>
    <div className="theme-picker">{Object.values(THEMES).map((theme) => <button type="button" key={theme.key} className={themeKey === theme.key ? "selected" : ""} onClick={() => setThemeKey(theme.key)}><span className={`theme-preview ${theme.key}`} /><strong>{theme.name}</strong><small>{theme.description}</small></button>)}</div>
    <div className="form-grid two"><label>Varsayılan dil<select value={defaultLocale} onChange={(event) => setDefaultLocale(event.target.value as Locale)}><option value="tr">Türkçe</option><option value="en">English</option></select></label><label>Medya kotası (MB)<input type="number" min={10} max={2048} value={quota} onChange={(event) => setQuota(Number(event.target.value))} /></label></div>
    {error && <div className="alert error">{error}</div>}
    <div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Vazgeç</button><button className="button primary" disabled={saving}>{saving ? "Oluşturuluyor…" : "Tenant oluştur"}</button></div>
  </form></div>;
}

function toSlug(value: string) {
  return value.toLocaleLowerCase("tr-TR").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").replaceAll("ı", "i").replace(/[^a-z0-9]+/gu, "-").replace(/^-+|-+$/gu, "");
}
